import React, { useState, useEffect, useMemo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import {
    TextField,
    Box,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Snackbar,
    Alert,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import documentService from '../services/documentService';
import ClientSelector from '../components/ClientSelector';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DescriptionIcon from '@mui/icons-material/Description';
import FileUpload from '../components/FileUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentPreviewer from '../components/DocumentPreviewer';
import api from '../services/api';

interface Document {
  name: string;
  file_name: string;
  size: number;
  updated: string;
  is_folder: boolean;
  document_date?: string; // Puede ser string (fecha) o undefined
}

interface Client {
  id: string;
  name: string;
}

registerLocale('es', es);

interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  document_date?: string;
}

const buildTree = (documents: Document[], clientId: string | null): TreeNode[] => {
    if (!clientId) return [];

    const rootNode: TreeNode = { id: 'root', name: 'root', children: [] };

    const uniqueFolders = new Set<string>();

    for (const doc of documents) {
        // Skip the root folder entry from S3 itself
        if (doc.file_name === '') continue;

        const pathParts = doc.file_name.replace(/\/$/, "").split('/');
        let parentNode = rootNode;

        for (let i = 0; i < pathParts.length; i++) {
            const part = pathParts[i];
            let childNode = parentNode.children!.find(child => child.name === part);

            if (!childNode) {
                const isLastPart = i === pathParts.length - 1;
                const isFolder = doc.is_folder || !isLastPart;
                
                const currentPathParts = pathParts.slice(0, i + 1);
                const nodeIdPath = currentPathParts.join('/');
                
                const fullNodeId = isFolder
                    ? `clients_storage/${clientId}/${nodeIdPath}/`
                    : doc.name;

                if (isFolder && uniqueFolders.has(fullNodeId)) {
                    parentNode = parentNode.children!.find(child => child.id === fullNodeId)!;
                    continue;
                }

                childNode = {
                    id: fullNodeId,
                    name: part,
                    children: isFolder ? [] : undefined,
                    document_date: doc.document_date,
                };

                if (isFolder) {
                    uniqueFolders.add(fullNodeId);
                }

                parentNode.children!.push(childNode);
                parentNode.children!.sort((a, b) => {
                    if (a.children && !b.children) return -1;
                    if (!a.children && b.children) return 1;
                    return a.name.localeCompare(b.name);
                });
            }
            
            if (childNode && childNode.children) {
                parentNode = childNode;
            }
        }
    }
    return rootNode.children || [];
};

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [selectedFileNode, setSelectedFileNode] = useState<TreeNode | null>(null);
  const [selectedFileForPreview, setSelectedFileForPreview] = useState<File | null>(null);
  const [auditFilter, setAuditFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ min_date: Date | null, max_date: Date | null }>({ min_date: null, max_date: null });

  const fetchDocuments = async (clientId: string, audited?: boolean) => {
    if (!clientId) {
      setDocuments([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await documentService.getDocuments(clientId, audited);
      setDocuments(response.data);
    } catch (err) {
      setError('Error al cargar los documentos.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para obtener el rango de fechas solo cuando cambia el cliente
  useEffect(() => {
    const fetchDateRange = async (clientId: string) => {
      try {
        const rangeResponse = await documentService.getDocumentDateRange(clientId);
        const { min_date, max_date } = rangeResponse.data;
        setDateRange({
          min_date: min_date ? new Date(min_date) : null,
          max_date: max_date ? new Date(max_date) : null,
        });
      } catch (err) {
        console.error("Error fetching date range:", err);
        setDateRange({ min_date: null, max_date: null });
      }
    };

    if (selectedClient) {
      fetchDateRange(selectedClient);
    } else {
      setDateRange({ min_date: null, max_date: null });
    }
  }, [selectedClient]);

  // Efecto para obtener los documentos cuando cambia cualquier filtro
  useEffect(() => {
    if (selectedClient) {
      let audited = auditFilter === 'all' ? undefined : auditFilter === 'audited';
      fetchDocuments(selectedClient, audited);
    } else {
      setDocuments([]);
    }
  }, [selectedClient, auditFilter, startDate, endDate]);

  const documentTree = useMemo(() => buildTree(documents, selectedClient), [documents, selectedClient]);

  const filterTreeByDate = (nodes: TreeNode[], startDate: Date | null, endDate: Date | null): TreeNode[] => {
    if (!startDate || !endDate) {
      return nodes;
    }

    const filteredNodes: TreeNode[] = [];

    for (const node of nodes) {
      if (node.children) { // Es una carpeta
        const filteredChildren = filterTreeByDate(node.children, startDate, endDate);
        if (filteredChildren.length > 0) {
          filteredNodes.push({ ...node, children: filteredChildren });
        }
      } else { // Es un archivo
        if (node.document_date) {
          const documentDate = new Date(node.document_date);
          if (documentDate >= startDate && documentDate <= endDate) {
            filteredNodes.push(node);
          }
        }
      }
    }
    return filteredNodes;
  };

  const filteredDocumentTree = useMemo(() => {
    if (auditFilter === 'audited' && startDate && endDate) {
      // Crear nuevas fechas para el inicio y el fin del día para evitar mutaciones
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      return filterTreeByDate(documentTree, start, end);
    }
    return documentTree;
  }, [documentTree, startDate, endDate, auditFilter]);

  const handleToggle = (nodeId: string) => {
    setExpandedNodes((prevExpanded) =>
      prevExpanded.includes(nodeId)
        ? prevExpanded.filter((id) => id !== nodeId)
        : [...prevExpanded, nodeId]
    );
  };

  const handleNodeClick = (node: TreeNode) => {
    if (node.children) { // It's a folder
      handleToggle(node.id);
      setSelectedPath(node.id);
      setSelectedFileNode(null);
      setPreviewUrl(null);
    } else { // It's a file
      const parentPath = node.id.substring(0, node.id.lastIndexOf('/') + 1);
      setSelectedPath(parentPath);
      setSelectedFileNode(node);
      handlePreview(node);
    }
  };

  const handlePreview = async (node: TreeNode) => {
    if (node.children) return; // No preview for folders

    setSelectedFileForPreview(null); // Limpiar el archivo local seleccionado
    setIsPreviewLoading(true);
    setPreviewError(null);
    setPreviewUrl(null);

    try {
      // Construct the public URL directly
      const publicUrl = documentService.getPublicUrl(node.id);
      setPreviewUrl(publicUrl);
    } catch (err: any) {
      setPreviewError('Error al construir la URL de vista previa.');
      console.error(err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleDelete = async (node: TreeNode) => {
    const isFolder = !!node.children;
    const itemName = node.name;
    const blobName = node.id; // El ID del nodo es el blob.name completo

    if (window.confirm(`¿Está seguro de que desea eliminar ${isFolder ? 'la carpeta' : 'el archivo'} "${itemName}"? ${isFolder ? 'Se eliminará todo su contenido.' : ''}`)) {
      try {
        setSubmitting(true);
        if (isFolder) {
          await documentService.deleteFolder(blobName);
        } else {
          await documentService.deleteDocument(blobName);
        }
        setSuccessMessage(`${isFolder ? 'Carpeta' : 'Archivo'} eliminado con éxito.`);
        // Refrescar la lista de documentos
        if (selectedClient) {
          fetchDocuments(selectedClient);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || `Error al eliminar ${isFolder ? 'la carpeta' : 'el archivo'}.`);
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const renderTree = (nodes: TreeNode[]) => {
    return (
      <ul className="file-tree">
        {nodes.map((node) => {
          const isExpanded = expandedNodes.includes(node.id);
          return (
            <li key={node.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, '&:hover .delete-icon': { visibility: 'visible' }, backgroundColor: (selectedPath === node.id || selectedFileNode?.id === node.id) ? '#f0f0f0' : 'transparent' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={() => handleNodeClick(node)}>
                  <Box sx={{ width: 40, textAlign: 'center', flexShrink: 0 }}>
                    {node.children && (isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />)}
                  </Box>
                  <Box sx={{ width: 40, textAlign: 'center', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <span style={{ flexShrink: 0 }}>{node.children ? '📁' : '📄'}</span>
                    <Typography sx={{ ml: 1 }}>
                      {node.name}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  className="delete-icon"
                  size="small"
                  onClick={() => handleDelete(node)}
                  sx={{ visibility: 'hidden' }}
                  disabled={submitting}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {isExpanded && Array.isArray(node.children) && node.children.length > 0 && renderTree(node.children)}
            </li>
          );
        })}
      </ul>
    );
  };

  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles);
    // Si se limpia la selección de archivos, también limpiar la vista previa
    if (newFiles.length === 0) {
      setSelectedFileForPreview(null);
      setPreviewUrl(null);
    }
  };

  const handleNewFilePreview = (file: File) => {
    // Limpiar la selección de archivo existente
    setSelectedFileNode(null);
    
    // Crear una URL local para el archivo
    const filePreviewUrl = URL.createObjectURL(file);
    
    // Actualizar los estados
    setSelectedFileForPreview(file);
    setPreviewUrl(filePreviewUrl);
    setIsPreviewLoading(false);
    setPreviewError(null);
  };

  const handleUpload = async () => {
    if (!selectedClient) {
      setError('Por favor, seleccione un cliente primero.');
      return;
    }
    if (files.length === 0) {
      setError('Por favor, suba al menos un archivo.');
      return;
    }

    setSubmitting(true);
    setError(null);

    // 1. Agrupar archivos por su ruta de directorio
    const filesByPath: { [key: string]: File[] } = {};
    files.forEach(file => {
        const relativePath = (file as any).webkitRelativePath || file.name;
        const lastSlash = relativePath.lastIndexOf('/');
        const path = lastSlash > -1 ? relativePath.substring(0, lastSlash) : '';
        
        if (!filesByPath[path]) {
            filesByPath[path] = [];
        }
        filesByPath[path].push(file);
    });

    // 2. Crear una promesa de subida para cada grupo
    const uploadPromises = Object.entries(filesByPath).map(([path, filesInPath]) => {
        const formData = new FormData();
        formData.append('client_id', selectedClient);
        
        // La ruta de destino combina la carpeta seleccionada en el árbol y la ruta relativa del archivo
        // The selectedPath is the full S3 prefix, so we need to extract the relative path from it.
        // The selectedPath is now the full S3 prefix for the destination folder.
        // We need to extract the path relative to the client's root folder.
        const clientRootPrefix = `clients_storage/${selectedClient}/`;
        let relativeUploadPath = '';
        if (selectedPath && selectedPath.startsWith(clientRootPrefix)) {
            relativeUploadPath = selectedPath.substring(clientRootPrefix.length);
        }
        
        const finalDestinationPath = [relativeUploadPath, path].filter(Boolean).join('/');
        formData.append('destination_path', finalDestinationPath);

        filesInPath.forEach(file => {
            // El nombre del archivo en el form-data no debe contener la ruta
            formData.append('files', file, file.name);
        });
        
        return documentService.createDocument(formData);
    });

    try {
        await Promise.all(uploadPromises);
        setSuccessMessage(`${files.length} archivo(s) y carpeta(s) subido(s) con éxito.`);
        setFiles([]); // Limpiar archivos después de la subida
        if (selectedClient) {
            fetchDocuments(selectedClient); // Refrescar la lista de documentos
        }
    } catch (err: any) {
        setError(err.response?.data?.detail || 'Ocurrió un error durante la subida de uno o más archivos.');
        console.error(err);
    } finally {
        setSubmitting(false);
    }
  };

  const handleCreateFolder = async () => {
    const folderName = prompt('Ingrese el nombre de la nueva carpeta:');
    if (folderName && selectedClient) {
      const clientRootPrefix = `clients_storage/${selectedClient}/`;
      let relativeUploadPath = '';
      if (selectedPath && selectedPath.startsWith(clientRootPrefix)) {
          relativeUploadPath = selectedPath.substring(clientRootPrefix.length);
      }
      
      const newFolderPath = [relativeUploadPath, folderName].filter(Boolean).join('/');

      try {
        setSubmitting(true);
        await documentService.createFolder(selectedClient, newFolderPath);
        setSuccessMessage(`Carpeta "${folderName}" creada con éxito.`);
        fetchDocuments(selectedClient);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error al crear la carpeta.');
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleDownload = async () => {
    if (!selectedClient) {
      setError('Por favor, seleccione un cliente para descargar los documentos.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await documentService.downloadDocuments(selectedClient);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `documentos_${selectedClient}.zip`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch.length === 2)
          fileName = fileNameMatch[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al descargar los documentos.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const treeStyles = `
    .file-tree, .file-tree ul {
      list-style: none;
      padding-left: 22px;
    }
    .file-tree li {
      position: relative;
    }
    .file-tree li::before {
      content: "";
      position: absolute;
      left: -15px;
      top: 0;
      width: 1px;
      height: 100%;
      background-color: #ccc;
    }
    .file-tree li::after {
      content: "";
      position: absolute;
      left: -15px;
      top: 18px;
      width: 15px;
      height: 1px;
      background-color: #ccc;
    }
    .file-tree li:last-child::before {
      height: 18px;
    }
  `;

  const handleClientChange = (clientName: string | null) => {
    setSelectedClient(clientName);
    setDocuments([]);
    setExpandedNodes([]);
    setSelectedPath('');
    setPreviewUrl(null);
    setSelectedFileNode(null);
    setFiles([]);
  };

  return (
    <>
    <style>{treeStyles}</style>
    <React.Fragment>
      <Typography variant="h4" gutterBottom>
        Gestión de Documentos
      </Typography>

      <ClientSelector selectedClient={selectedClient} onClientChange={handleClientChange} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, height: 'calc(100vh - 270px)' }}>
        {/* Columna 1: Carga de Documentos */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', opacity: !selectedClient ? 0.5 : 1, pointerEvents: !selectedClient ? 'none' : 'auto', flexBasis: '33.33%', flexShrink: 0, overflow: 'hidden' }}>
            <Typography variant="h6" gutterBottom>
                Cargar en: <Typography component="span" color="primary.main" sx={{ wordBreak: 'break-all' }}>
                    {(() => {
                      const clientName = selectedClient;
                      if (clientName) {
                        const clientRootPrefix = `clients_storage/${clientName}/`;
                        if (selectedPath && selectedPath.startsWith(clientRootPrefix)) {
                          return selectedPath.substring(clientRootPrefix.length).replace(/\/$/, '') || clientName;
                        }
                        return clientName; // Default to the client's root name
                      }
                      return ''; // Default text if no client is selected
                  })()}
              </Typography>
          </Typography>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <FileUpload
                  onFilesChange={handleFilesChange}
                  onFileSelect={handleNewFilePreview}
                  selectedFile={selectedFileForPreview}
                />
              </Box>
              <Box sx={{ mt: 2, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" onClick={handleUpload} disabled={files.length === 0 || submitting || !selectedClient} size="large" sx={{ fontWeight: 500, textTransform: 'none' }}>
                      {submitting ? <CircularProgress size={24} color="inherit" /> : 'Subir Documentos'}
                  </Button>
              </Box>
          </Box>
        </Paper>

        {/* Columna 2: Documentos Existentes */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Documentos Existentes</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {auditFilter === 'audited' && selectedClient && (
                        dateRange.min_date ? (
                            <>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    placeholderText="Desde"
                                    customInput={<TextField size="small" sx={{ m: 1 }} />}
                                    locale="es"
                                    minDate={dateRange.min_date || undefined}
                                    maxDate={dateRange.max_date || undefined}
                                />
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || dateRange.min_date || undefined}
                                    maxDate={dateRange.max_date || undefined}
                                    placeholderText="Hasta"
                                    customInput={<TextField size="small" sx={{ m: 1 }} />}
                                    locale="es"
                                />
                            </>
                        ) : (
                            <Typography sx={{ m: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                No hay documentos auditados con fechas.
                            </Typography>
                        )
                    )}
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <InputLabel>Filtro</InputLabel>
                      <Select
                        value={auditFilter}
                        label="Filtro"
                        onChange={(e) => setAuditFilter(e.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="audited">Auditados</MenuItem>
                        <MenuItem value="unaudited">No Auditados</MenuItem>
                      </Select>
                    </FormControl>
                    <Button variant="outlined" color="primary" startIcon={<CreateNewFolderIcon />} onClick={handleCreateFolder} disabled={!selectedClient || submitting} sx={{ mr: 2, textTransform: 'none' }}>
                        Crear Carpeta
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDownload} disabled={!selectedClient || documents.length === 0 || submitting} size="large" sx={{ fontWeight: 500, textTransform: 'none' }}>
                        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Descargar'}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ flexGrow: 1, mt: 1, p: 1, overflow: 'auto' }} onClick={(e) => {
                if (e.target === e.currentTarget) {
                    setSelectedPath('');
                    setSelectedFileNode(null);
                }
            }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" sx={{ mt: 2 }}><CircularProgress /></Box>
                ) : selectedClient ? (
                    renderTree(filteredDocumentTree)
                ) : (
                    <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                       <Typography>Seleccione un cliente para ver sus documentos.</Typography>
                    </Box>
                )}
            </Box>
        </Paper>

        {/* Columna 3: Vista Previa */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flexBasis: '33.33%', flexShrink: 0, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Vista Previa</Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                {isPreviewLoading ? (
                    <CircularProgress />
                ) : previewError ? (
                    <Alert severity="error">{previewError}</Alert>
                ) : previewUrl ? (
                    <DocumentPreviewer
                        fileUrl={previewUrl}
                        fileName={selectedFileForPreview?.name || selectedFileNode?.name || ''}
                    />
                ) : (
                    <Typography>Seleccione un archivo para ver la vista previa.</Typography>
                )}
            </Box>
        </Paper>
      </Box>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </React.Fragment>
    </>
  );
};

export default DocumentsPage;